import { FieldId } from '~/evifields/src/types/FieldId';
import {
  LabeledOperator,
  SearchFilterType,
} from '~/features/advanced-search/types';
import { eviSearch } from '~/services/serviceInstances';
import { ColumnSortOrder } from '~/types/Columns';
import { Nullable } from '~/types/Nullable';
import { SearchTextMatch } from '~/types/Search';
import { Uuid } from '~/types/Uuid';

import { JsonApiGetResponse, JsonApiListResponse } from '../ApiTypes';
import {
  ApiChartData,
  ChartData,
  CreateChartParams,
} from '../types/dashboard/charts';
import { toSortParam } from '../utils';

export type SearchType = 'ticket' | 'workflow' | 'document';

export type FieldValueTypes = string | string[] | number;
export interface SearchParams {
  query: Array<any>;
  filters?: Array<FieldId>;
  sortBy: ColumnSortOrder;
  page: number;
  pageSize: number;
  type: SearchType;
}

export type SearchItemValue = {
  value: FieldValueTypes;
  value_structured: any[];
};

export type SearchItem<V = SearchItemValue> = {
  highlights: {
    full_text_matches: SearchTextMatch;
  };
  permissions: unknown; //TBD
  selected_field_values: Record<FieldId, V>;
  id: Uuid;
  type: SearchType;
};

interface SearchResponseResult {
  query: Array<any>; //TBD
  results: Array<SearchItem>;
}

interface Links {
  next: string;
  previous: string;
}

export interface SearchMeta {
  total: number;
  page: {
    size: number;
    current: number;
    next: number;
  };
}

export interface SearchResult<V = SearchItemValue> {
  results?: SearchItem<V>[];
  meta?: SearchMeta;
}

export interface SearchFilterApiResponse {
  section: string;
  label: string;
  help_text: string;
  type: SearchFilterType;
  operators: LabeledOperator[];
  is_chartable: boolean;
  is_requestable_column: boolean;
  clause_text_search?: {
    id: string;
    label: string;
    type: string;
    operators: LabeledOperator[];
  };
}

interface SuggestionResponse {
  label: string;
  value: string;
  count: number;
}

interface SuggestionsMeta {
  select_all_count: number;
}

interface ExportOptionsParams {
  query: Array<any>;
  type: SearchType;
}

interface ExportExcelParams {
  query: Array<any>;
  type: SearchType;
  fields: Nullable<Array<string>>;
}

export const getFieldValue = (value: FieldValueTypes) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (Array.isArray(value)) {
    return value?.join(', ');
  }
  return undefined;
};

export const search = async ({
  page,
  type,
  pageSize,
  sortBy,
  query,
  filters = [],
}: SearchParams) => {
  const response: JsonApiGetResponse<
    'search',
    SearchResponseResult,
    SearchMeta,
    void,
    Links
  > = await eviSearch.post(
    '/search',
    {
      data: {
        type,
        attributes: {
          query,
          filters,
        },
      },
    },
    {
      params: {
        'page[number]': page,
        'page[size]': pageSize,
        highlighterType: 'unified',
        sort: toSortParam(sortBy),
      },
    },
  );

  return {
    results: response?.data.attributes.results.map((searchItem) => searchItem),
    meta: response?.meta,
  };
};

export const fetchFiltersV2: ({
  type,
}: {
  type: SearchType;
}) => Promise<JsonApiListResponse<'filter', SearchFilterApiResponse>> = async ({
  type,
}: {
  type: 'ticket' | 'workflow' | 'document';
}) => {
  return eviSearch.get('/filters', { params: { entity: type } });
};

export const getFilterSuggestionsV2 = async ({
  filterId,
  type,
  query,
}: {
  filterId: string;
  type: SearchType;
  query: any;
}) => {
  const response = (await eviSearch.get(`/filters/${filterId}/suggestions`, {
    params: { entity: type, query },
  })) as JsonApiListResponse<
    'suggestion',
    SuggestionResponse,
    SuggestionsMeta,
    void
  >;

  return {
    data: response.data,
    meta: response.meta,
  };
};

const createChart = ({
  uuid,
  query,
  bucket_size = 10,
  entity,
  interval,
}: CreateChartParams): Promise<JsonApiGetResponse<SearchType, ApiChartData>> =>
  eviSearch.post(`/search/chart/${uuid}`, {
    data: {
      type: entity,
      attributes: { query, bucket_count: bucket_size, interval },
    },
  });

export const getChartDataV2 = async (
  params: CreateChartParams,
): Promise<ChartData> => {
  const response = await createChart(params);

  const { buckets: apiBuckets, bucket_count: bucketCount } =
    response!.data.attributes;
  return {
    bucketCount,
    buckets: apiBuckets.map((b) => ({
      label: b.label,
      value: b.count,
      bucketValue: b.value,
    })),
    meta: response?.meta,
  };
};

export const getSearchExportOptionsV2 = async ({
  query,
  type,
}: ExportOptionsParams) => {
  const response = await eviSearch.post(`/search/export-options`, {
    data: { attributes: { query, type } },
  });
  return response.data.attributes;
};

export const exportExcelV3 = ({
  type,
  query,
  fields = [],
}: ExportExcelParams) => {
  return eviSearch.post(
    '/search/export',
    {
      data: { attributes: { query, type, fields } },
    },
    {
      responseType: 'arraybuffer',
      // @ts-ignore RTKQ missing mapping of this property
      fullResponse: true,
    },
  );
};

export const validateBooleanQueryEvisearch = async (
  query: string,
): Promise<{ detail: string }> => {
  return eviSearch.get('/validate-bool-query', {
    params: { query },
  });
};
